/**
 * Security utility functions for input validation and sanitization
 */

// Regular expressions for validation
const PATTERNS = {
    EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    PHONE: /^\+?[\d\s-]{10,}$/,
    NAME: /^[a-zA-Z\s'-]{2,50}$/,
    URL: /^https?:\/\/[\w-]+(\.[\w-]+)+[/#?]?.*$/,
    HTML_TAGS: /<[^>]*>/g,
    SCRIPTS: /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    DANGEROUS_ATTRS: /on\w+\s*=|javascript:|data:/gi,
};

/**
 * Sanitize string input by removing HTML tags and dangerous content
 * @param {string} value - Input string to sanitize
 * @returns {string} - Sanitized string
 */
export const sanitizeString = (value) => {
    if (typeof value !== 'string') return value;

    // Remove HTML tags
    let sanitized = value.replace(PATTERNS.HTML_TAGS, '');
    
    // Remove script tags and dangerous attributes
    sanitized = sanitized
        .replace(PATTERNS.SCRIPTS, '')
        .replace(PATTERNS.DANGEROUS_ATTRS, '');
    
    // NOTE: We intentionally do NOT convert characters to HTML entities here.
    // HTML entity encoding (e.g., ' → &#39;) is for DOM rendering, not for API data.
    // Converting here corrupts stored text (e.g., "user's report" → "user&#39;s report").
    // The HTML tag stripping above is sufficient for XSS prevention in API payloads.

    return sanitized.trim();
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid
 */
export const isValidEmail = (email) => PATTERNS.EMAIL.test(email);

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid
 */
export const isValidPhone = (phone) => PATTERNS.PHONE.test(phone);

/**
 * Validate name format
 * @param {string} name - Name to validate
 * @returns {boolean} - True if valid
 */
export const isValidName = (name) => PATTERNS.NAME.test(name);

/**
 * Check if a string contains potentially malicious content
 * @param {string} value - String to check
 * @returns {Object} - Object containing safety status and details
 */
export const checkSecurity = (value) => {
    if (typeof value !== 'string') return { safe: true };

    const issues = [];

    if (PATTERNS.HTML_TAGS.test(value)) {
        issues.push('HTML tags detected');
    }
    if (PATTERNS.SCRIPTS.test(value)) {
        issues.push('Script tags detected');
    }
    if (PATTERNS.DANGEROUS_ATTRS.test(value)) {
        issues.push('Dangerous attributes or protocols detected');
    }

    return {
        safe: issues.length === 0,
        issues
    };
};

/**
 * Sanitize an entire form data object
 * @param {Object} data - Form data object
 * @returns {Object} - Sanitized data and any security issues found
 */
export const sanitizeFormData = (data) => {
    const sanitized = {};
    const issues = [];

    for (const [key, value] of Object.entries(data)) {
        if (Array.isArray(value)) {
            sanitized[key] = value.map(item => 
                item instanceof File ? item : (typeof item === 'string' ? sanitizeString(item) : item)
            );
        } else if (value && typeof value === 'object') {
            const result = sanitizeFormData(value);
            sanitized[key] = result.sanitized;
            if (result.issues.length) {
                issues.push(...result.issues.map(issue => ({
                    field: `${key}.${issue.field}`,
                    ...issue
                })));
            }
        } else if (typeof value === 'string') {
            // Do not sanitize archive passwords to avoid altering password characters
            if (key === 'archive_password') {
                sanitized[key] = value.trim();
            } else {
                sanitized[key] = sanitizeString(value);
                const securityCheck = checkSecurity(value);
                if (!securityCheck.safe) {
                    issues.push({
                        field: key,
                        issues: securityCheck.issues
                    });
                }
            }
        } else {
            sanitized[key] = value;
        }
    }

    return { sanitized, issues };
};